import { Map, Overlay, View } from "ol";
import {
  Control,
  defaults as defaultControls,
  DefaultsOptions,
} from "ol/control";
import Zoom from "ol/control/Zoom";
import LayerGroup from "ol/layer/Group";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { LayerTitle } from "../types/layers";
import { CustomMapEventTypes } from "../types/map";
import useCreateMapLayers from "./useCreateMapLayers";

interface CreateMapArgs {
  target: string | HTMLElement;
  includedLayers?: LayerTitle[];
  popup?: boolean;
  extraControls?: Control[];
  extraControlOptions?: DefaultsOptions;
  existingMap: Map | undefined;
}

interface UseCreateMapReturn {
  createMap: ({
    target,
    includedLayers,
    popup,
    extraControls,
    extraControlOptions,
    existingMap,
  }: CreateMapArgs) => Map;
}

function useCreateMap(): UseCreateMapReturn {
  const { t } = useTranslation("map");
  const { createMapLayers } = useCreateMapLayers();

  const createMap = useCallback(
    ({
      target,
      includedLayers,
      popup = true,
      extraControls,
      extraControlOptions,
      existingMap,
    }: CreateMapArgs): Map => {
      let overlay: Overlay[] | undefined;
      if (popup && !existingMap?.getOverlayById("popup")) {
        overlay = [
          new Overlay({
            element: document.getElementById("popup") || undefined,
            id: "popup",
          }),
        ];
      }

      const zoom =
        existingMap
          ?.getControls()
          .getArray()
          .find((c) => c instanceof Zoom) ??
        new Zoom({
          zoomInTipLabel: t("zoom-in"),
          zoomOutTipLabel: t("zoom-out"),
        });
      const defaultControlOptions: DefaultsOptions = { zoom: false };
      
      const layers = createMapLayers(includedLayers);
      const controls = defaultControls({
        ...defaultControlOptions,
        attributionOptions: { collapsible: true, collapsed: true },
        ...extraControlOptions,
      })
        .extend([zoom])
        .extend(extraControls ?? []);
      const overlays = overlay;

      // Create a new map if none exist
      if (!existingMap) {
        const map = new Map({
          layers,
          target,
          view: new View({
            center: [2706137.2374070706, 8527801.660336273],
            zoom: 9,
          }),
          controls,
          overlays,
        });
        return map;
      }
      // If map exists, only update some properties
      existingMap
        .getLayers()
        .getArray()
        .filter((l) => l.get("title"))
        .forEach((l) => {
          existingMap.removeLayer(l);
        });
      existingMap.setLayerGroup(new LayerGroup({ layers }));
      existingMap.dispatchEvent(CustomMapEventTypes.SettingsChanged);
      existingMap.dispatchEvent(CustomMapEventTypes.FeaturesAdded);
      return existingMap;
    },
    [t, createMapLayers],
  );

  return { createMap };
}

export default useCreateMap;
